import { CheckCircle2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';

interface NotificationModalProps {
  visible: boolean;
  type: 'success' | 'error';
  title: string;
  message: string;
  onClose: () => void;
}

export function NotificationModal({ visible, type, title, message, onClose }: NotificationModalProps) {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (visible) {
      // Delay untuk animasi smooth
      setTimeout(() => setShow(true), 10);
    } else {
      setShow(false);
    }
  }, [visible]);

  if (!visible) return null;

  const isSuccess = type === 'success';
  const Icon = isSuccess ? CheckCircle2 : XCircle;
  const iconColor = isSuccess ? 'text-green-500' : 'text-red-500';
  const bgColor = isSuccess ? 'bg-green-50 dark:bg-green-900/20' : 'bg-red-50 dark:bg-red-900/20';
  const borderColor = isSuccess ? 'border-green-200 dark:border-green-500/30' : 'border-red-200 dark:border-red-500/30';

  return (
    <div 
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        show ? 'bg-black/40 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={onClose}
    >
      <div
        className={`bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all duration-300 ${
          show ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon Section */}
        <div className={`${bgColor} border-b ${borderColor} py-6 flex justify-center`}>
          <div className={`relative ${show ? 'animate-bounce-once' : ''}`}>
            <Icon className={`h-20 w-20 ${iconColor}`} strokeWidth={1.5} />
            {isSuccess && show && (
              <div className="absolute inset-0 animate-ping">
                <CheckCircle2 className="h-20 w-20 text-green-400 opacity-75" strokeWidth={1.5} />
              </div>
            )}
          </div>
        </div>

        {/* Content Section */}
        <div className="p-6 space-y-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 text-center">
            {title}
          </h3>
          <p className="text-gray-600 dark:text-gray-300 text-center whitespace-pre-line">
            {message}
          </p>
        </div>

        {/* Button Section */}
        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className={`w-full py-3 px-4 rounded-lg font-medium transition-all transform active:scale-95 ${
              isSuccess
                ? 'bg-green-500 hover:bg-green-600 text-white shadow-lg shadow-green-500/30'
                : 'bg-red-500 hover:bg-red-600 text-white shadow-lg shadow-red-500/30'
            }`}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// Custom CSS untuk animasi bounce sekali
// Tambahkan ke tailwind.config.js jika belum ada:
// animation: {
//   'bounce-once': 'bounce 0.6s ease-in-out 1',
// }
